import { AnalyticsProperties } from '@play-co/gcinstant';
import { GCInstant } from 'src/lib/gcinstant';
import { dev_fetchAllIDsOffline } from '@play-co/replicant';
import { View } from '@play-co/timestep-core/ui';
import StateObserver, { user } from 'src/StateObserver';
import { captureGenericError } from 'src/lib/sentry';
import { isPlatformGuest } from 'src/lib/utils';
import { SocialFriendProfile } from './social.sequence';
import { isFirstEntry } from 'src/lib/platformUtils';
import { Feature } from 'src/replicant/analytics';
import { track } from 'src/lib/analytics/utils';
import { createEmitter } from 'src/lib/Emitter';
import {
  deriveFriendAddAttribution,
  getBuruFriends,
  getBuruFriendsByRecency,
  getBuruFriendsCount,
  isBuruFriend,
  wasUnfriended,
} from 'src/replicant/friends/friends.getters';
import { getPlayersProfiles } from '../friends/friends.profiles';
import { cardsIsCardShareEntryData } from '../cards/cards.share.entry';

export let socialFriends: Promise<{
  affinity: SocialFriendProfile[];
  nonAffinity: SocialFriendProfile[];
}> = Promise.resolve({
  affinity: [],
  nonAffinity: [],
});

/**
 * `getFriendAnalytics` for an empty `friendIds`. The player counts in their own
 * cluster, so each cluster size is one rather than zero.
 */
const FRIENDLESS_ANALYTICS: Parameters<typeof GCInstant.setFriendAnalytics>[0] =
  {
    activeFriendCounts: {
      playerFriendCount: 0,
      activeFriendCount1D: 0,
      activeFriendCount3D: 0,
      activeFriendCount7D: 0,
      activeFriendCount14D: 0,
      activeFriendCount30D: 0,
      activeFriendCount90D: 0,
    },
    ageGroupDistribution: { adult: 0, teen: 0, unknown: 0 },
    clusterCounts: {
      clusterSize1D: 1,
      clusterSize3D: 1,
      clusterSize7D: 1,
      clusterSize14D: 1,
      clusterSize30D: 1,
      clusterSize90D: 1,
      clusterConnections1D: 0,
      clusterConnections3D: 0,
      clusterConnections7D: 0,
      clusterConnections14D: 0,
      clusterConnections30D: 0,
      clusterConnections90D: 0,
      clusterConnectedness1D: 0,
      clusterConnectedness3D: 0,
      clusterConnectedness7D: 0,
      clusterConnectedness14D: 0,
      clusterConnectedness30D: 0,
      clusterConnectedness90D: 0,
    },
  };

/**
 *  Update social graph
 * @param view
 * @param inviterId Inviter this entry is about to befriend, counted towards friend
 * analytics because the friendship is only established after `EntryFinal` is sent.
 */
export async function updateSocialGraph(view: View, inviterId?: string) {
  try {
    // Friend every local profile
    const mockFriends = initializeMockFriends();
    const friendIds = [...getBuruFriends(user()), ...mockFriends];

    if (inviterId && !friendIds.includes(inviterId)) {
      friendIds.push(inviterId);
    }

    // Update friends async if friends state was changed
    createEmitter(view, ({ user }) => getBuruFriends(user)).addListener(
      () => refreshExternalFriends(),
      { noInit: true }, // We want to avoid initial update
    );

    // This await sits between `startGameAsync` and `EntryFinal`, and a player
    // with no friends needs no round trip to be told so.
    const friendAnalytics = friendIds.length
      ? await StateObserver.replicant.asyncGetters.getFriendAnalytics({
          friendIds,
        })
      : FRIENDLESS_ANALYTICS;

    // Set friends analytics, should be set before sending entry finale analytics
    GCInstant.setFriendAnalytics(friendAnalytics);

    // ONLY FOR DEV !!!
    // This trick solve issue with production friends on dev/alpha/qa environments
    // If you use production friends on dev envs you may got issues with api.postMessage
    // when trying to send message to players that not exist on DB
    // fetchStates will create empty states for not existed user
    // See conversation: https://playcoinc.slack.com/archives/C02ALN39W/p1655847248363799
    if (process.env.IS_DEVELOPMENT) {
      await StateObserver.replicant.fetchStates([...friendIds]);
    }
  } catch (error) {
    console.warn('Updating social graph failed', error);
    captureGenericError(
      'Updating social graph failed',
      (error as Error) ?? null,
    );
  }
}

/**
 * Refresh the GCInstant external-friends cache from the current Replicant
 * buruFriends state. Best-effort: never throws — failures are logged to
 * Sentry and swallowed, leaving the cache as it was.
 *
 * Friends are passed newest first so the profile fetch cap drops the oldest
 * friendships rather than arbitrary ones.
 */
export async function refreshExternalFriends() {
  try {
    const profiles = await getPlayersProfiles(getBuruFriendsByRecency(user()));
    GCInstant.updateExternalFriends(profiles);
  } catch (error) {
    captureGenericError(
      'Failed to refresh external friends',
      (error as Error) ?? null,
    );
  }
}

/**
 * Initialize mock friends for offline mode.
 */
export function initializeMockFriends() {
  if (process.env.REPLICANT_OFFLINE && !isPlatformGuest(GCInstant)) {
    // Retrieve all user ids
    const userIds = dev_fetchAllIDsOffline(StateObserver.replicant!);

    // Remove self from the list
    const friendIds = userIds.filter((x) => x !== GCInstant.playerID);

    // Make everyone friends
    GCInstant.friendIds = friendIds;

    const friends = [...GCInstant.friends];

    // Add the missing friends to GCInstant
    friendIds.forEach((friendId) => {
      const friend = friends.find((friend) => friend.id === friendId);

      if (friend) {
        return;
      }

      // Importing friend here will actually break the game
      // Just add the necessary properties as any
      GCInstant.friends.push({
        id: friendId,
        name: 'Player ' + friendId,
        photo:
          'https://storage.googleapis.com/devkit-instant/avatar/animal_0001.png',
      } as any);

      GCInstant.friendsMap[friendId] = {
        id: friendId,
        name: 'Player ' + friendId,
        photo:
          'https://storage.googleapis.com/devkit-instant/avatar/animal_0001.png',
      } as any;
    });

    // Stay inside the visit search's 7 day liveness window.
    window.addEventListener('beforeunload', async () => {
      await StateObserver.invoke.cheat_refreshOfflineUpdateAt();
    });

    return friendIds;
  }
  return [];
}
export async function getSocialFriendsList(): typeof socialFriends {
  return socialFriends;
}

/**
 * Register referral first login and send rewards to referrer.
 * It might not necessarily happen on login or entry final - it's currently happening after first puzzle completion.
 */
export async function registerReferralFirstEntry() {
  // Only happens on first entry
  if (!isFirstEntry()) return;

  // Skip if there is no entry data
  const entryData = GCInstant.entryData as AnalyticsProperties.EntryData & {
    rewardOnConversion?: boolean;
  };
  if (!entryData) return;

  if (cardsIsCardShareEntryData(entryData)) {
    return;
  }

  // Store the inviter
  let inviter: string | undefined;

  // Check if there is a web referrer, from shared links
  if (entryData.webReferrer) {
    inviter = entryData.webReferrer;
  }

  // Check if there is a referrer from regular invite
  if (
    !inviter &&
    entryData.playerID &&
    entryData.feature === Feature.INVITE._ &&
    entryData.rewardOnConversion
  ) {
    inviter = entryData.playerID;
  }

  // If there is no inviter, just ignore it and return
  if (!inviter) return;

  /**
   * If inviter is self, just ignore it and return.
   * This should not happen for users, but can happen for testers who reset their account.
   */
  if (inviter === user().id) return;

  // If there is an inviter, trigger the referral first login, rewarding the inviter
  await StateObserver.invoke.referralFirstLogin({ inviter });

  // Mirror origin attribution from the entry payload via the canonical helper.
  const { originFeature, originSubFeature, method } =
    deriveFriendAddAttribution(entryData);

  // Log analytics event
  track('FriendAdd', {
    friendCount: getBuruFriendsCount(user()),
    method,
    originFeature,
    originSubFeature,
    friendRequestReceiverID: user().id,
    friendRequestSenderID: inviter,
    isReceiver: true,
  });
}

/**
 * Handle referral subsequent login, adding the inviter as a friend if not already
 * if in-game friends feature flag is enabled.
 */
export async function handleExistingUserReferral() {
  // Only happens for non first entries
  if (isFirstEntry()) return;

  // Skip if there is no entry data
  const entryData = GCInstant.entryData as AnalyticsProperties.EntryData & {
    rewardOnConversion?: boolean;
  };
  if (!entryData) return;

  if (cardsIsCardShareEntryData(entryData)) {
    return;
  }

  // Store the inviter
  let inviter: string | undefined;

  // Check if there is a web referrer, from shared links
  if (entryData.webReferrer) {
    inviter = entryData.webReferrer;
  }

  // Check if there is a referrer from regular invite
  if (
    !inviter &&
    entryData.playerID &&
    entryData.feature === Feature.INVITE._ &&
    entryData.rewardOnConversion
  ) {
    inviter = entryData.playerID;
  }

  // If there is no inviter, just ignore it and return
  if (!inviter) return;

  // If inviter is self, just ignore it and return
  if (inviter === user().id) return;

  // Check if friendship will actually be established
  // (mirrors server-side guard conditions in referralSubsequentLogin)
  const alreadyFriend = isBuruFriend(user(), inviter);
  const previouslyUnfriended = wasUnfriended(user(), inviter);

  // If there is an inviter, trigger the referral subsequent login,
  // adding the inviter as a friend if not already
  await StateObserver.invoke.referralSubsequentLogin({ inviter });

  // Fire FriendAdd only if friendship was actually established
  if (!alreadyFriend && !previouslyUnfriended) {
    // Mirror origin attribution from the entry payload via the canonical helper.
    const { originFeature, originSubFeature, method } =
      deriveFriendAddAttribution(entryData);

    track('FriendAdd', {
      friendCount: getBuruFriendsCount(user()),
      method,
      originFeature,
      originSubFeature,
      friendRequestReceiverID: user().id,
      friendRequestSenderID: inviter,
      isReceiver: true,
    });
  }
}
